import { create } from "zustand";
import { devtools } from "zustand/middleware";
import * as SavedDistributorProductApi from "@src/api/SavedDistributorProductApi";
import { PaginationValues } from "@src/types/pagination";
import { SavedDistributorProduct } from "@src/types/savedDistributorProduct";

const DEFAULT_PAGINATION: PaginationValues = {
  pageSize: 20,
  page: 1,
  totalPages: 0,
  totalCount: 0,
};

type SavedForLaterState = {
  savedForLaters: SavedDistributorProduct[] | null;
  loading: boolean;
  error: string | null;
  pagination: PaginationValues;
  fetchSavedForLater: (
    paginationOverrides?: Partial<PaginationValues>,
  ) => Promise<void>;
  refreshSavedForLater: () => Promise<void>;
  setSavedForLater: (savedForLaters: SavedDistributorProduct[]) => void;
  setPagination: (pagination: PaginationValues) => void;
};

export const useSavedForLaterStore = create<SavedForLaterState>()(
  devtools((set, get) => {
    const store: SavedForLaterState = {
      savedForLaters: null,
      loading: false,
      error: null,
      pagination: DEFAULT_PAGINATION,

      fetchSavedForLater: async (paginationOverrides = {}) => {
        set(
          { loading: true, error: null },
          false,
          "savedForLaters/fetch-start",
        );

        try {
          const currentPagination = get().pagination;
          const nextPagination = {
            ...currentPagination,
            ...paginationOverrides,
          };
          const response =
            await SavedDistributorProductApi.getSavedForLaterWithLivePricing(
              nextPagination,
            );
          const body = response.body;
          const savedForLaters = Array.isArray(body)
            ? body
            : (body?.results ?? []);
          const totalCount =
            body?.totalCount ??
            body?.total_count ??
            body?.count ??
            savedForLaters.length;
          const totalPages = body?.totalPages ?? body?.total_pages ?? 1;
          const page = body?.page ?? nextPagination.page;
          set(
            {
              savedForLaters,
              loading: false,
              pagination: {
                ...nextPagination,
                page,
                totalCount,
                totalPages,
              },
            },
            false,
            "savedForLaters/fetch-success",
          );
        } catch (err: any) {
          set(
            {
              error: err.message || "Unknown error while fetching saved items",
              loading: false,
            },
            false,
            "savedForLaters/fetch-error",
          );
        }
      },

      refreshSavedForLater: async () => {
        try {
          const response =
            await SavedDistributorProductApi.getSavedForLaterWithLivePricing(
              get().pagination,
            );
          const body = response.body;
          const savedForLaters = Array.isArray(body)
            ? body
            : (body?.results ?? []);
          const totalCount =
            body?.totalCount ??
            body?.total_count ??
            body?.count ??
            savedForLaters.length;
          const totalPages = body?.totalPages ?? body?.total_pages ?? 1;
          const page = body?.page ?? get().pagination.page;
          set(
            {
              savedForLaters,
              pagination: {
                ...get().pagination,
                page,
                totalCount,
                totalPages,
              },
            },
            false,
            "savedForLaters/refresh",
          );
        } catch (err: any) {
          console.error("Failed to refresh saved items:", err);
          set(
            {
              error:
                err.message || "Unknown error while refreshing saved items",
            },
            false,
            "savedForLaters/refresh-error",
          );
        }
      },

      setSavedForLater: (
        savedDistributorProducts: SavedDistributorProduct[],
      ) => {
        set(
          {
            savedForLaters: savedDistributorProducts,
          },
          false,
          "savedForLaters/set",
        );
      },

      setPagination: (pagination: PaginationValues) => {
        set({ pagination }, false, "savedForLaters/set-pagination");
      },
    };

    return store;
  }),
);
